// Push Notification Service for LavoroTicino PWA

const API_URL = process.env.REACT_APP_BACKEND_URL;
const isDev = process.env.NODE_ENV === 'development';

// Convert VAPID public key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// Check if notifications are supported
export function isNotificationSupported() {
  return 'Notification' in window;
}

// Get current notification permission status
export function getNotificationPermission() {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

// Register Service Worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    if (isDev) console.log('Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    if (isDev) console.error('Service Worker registration failed:', error);
    return null;
  }
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!isNotificationSupported()) {
    return { granted: false, reason: 'unsupported' };
  }
  
  if (Notification.permission === 'granted') {
    return { granted: true, reason: 'already_granted' };
  }
  
  if (Notification.permission === 'denied') {
    return { granted: false, reason: 'denied' };
  }
  
  try {
    const permission = await Notification.requestPermission();
    return { granted: permission === 'granted', reason: permission };
  } catch (error) {
    return { granted: false, reason: 'error' };
  }
}

// Subscribe to push notifications
export async function subscribeToPush(userId, userType) {
  if (!isPushSupported()) {
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Get VAPID public key from server
    const response = await fetch(`${API_URL}/api/push/vapid-public-key`);
    if (!response.ok) {
      return null;
    }
    
    const { publicKey } = await response.json();
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
    
    // Send subscription to server
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        user_id: userId,
        user_type: userType
      })
    });
    
    if (isDev) console.log('Push subscription successful');
    return subscription;
  } catch (error) {
    if (isDev) console.error('Push subscription failed:', error);
    return null;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(userId) {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId })
      });
    }
    
    return true;
  } catch (error) {
    if (isDev) console.error('Push unsubscription failed:', error);
    return false;
  }
}

// Update app badge count
export async function updateBadge(count) {
  // Update via Service Worker
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'SET_BADGE',
      count: count
    });
  }
  
  // Direct badge API (if supported)
  if ('setAppBadge' in navigator) {
    try {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
    } catch (error) {
      // Badge API not available or failed silently
    }
  }
}

// Show local notification (when app is open)
export function showLocalNotification(title, options = {}) {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return;
  }
  
  const notification = new Notification(title, {
    icon: '/icons/icon-192x192.png',
    badge: '/icons/icon-76x76.png',
    ...options
  });
  
  notification.onclick = () => {
    window.focus();
    notification.close();
    if (options.url) {
      window.location.href = options.url;
    }
  };
  
  return notification;
}
